import { useState } from 'react';
import {
  Alert,
  Box,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CustomSelect,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  PaginationTable,
} from '../../../../common/components';
import { formatCurrency } from '../../../../utils/formatUtils';
import { useUpdateDebtStatus } from '../../../../hooks/useDebt';
import { GERENCIALES_DEBT_STATUS_OPTIONS } from '../../constants/gerencialesConstants';

/**
 * Renderiza la tabla de mora con estado editable.
 * @param {Object} props - Props del componente.
 * @param {Array<Object>} props.rows - Filas del reporte.
 * @param {number} props.totalContracts - Total de contratos.
 * @param {Function} props.onReload - Handler de recarga.
 * @param {Object} props.pagination - Datos de paginación.
 * @param {Function} props.onPageChange - Handler de página.
 * @param {Function} props.onRowsPerPageChange - Handler de items por página.
 * @returns {JSX.Element} Tabla de mora.
 */
const DebtStatusTable = ({
  rows,
  totalContracts,
  onReload,
  pagination,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const updateDebtStatus = useUpdateDebtStatus();
  const [updatingContract, setUpdatingContract] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /**
   * Actualiza el estado de deuda del contrato.
   * @param {Object} row - Fila del contrato.
   * @param {string} nextStatus - Estado seleccionado.
   * @returns {Promise<void>} Promesa de actualización.
   */
  const handleStatusChange = async (row, nextStatus) => {
    if (!row?.contract_uuid || row.debt_status === nextStatus) {
      return;
    }

    setUpdatingContract(row.contract_uuid);
    try {
      await updateDebtStatus.mutateAsync({
        contractUuid: row.contract_uuid,
        debt_status: nextStatus,
      });
      setSnackbar({
        open: true,
        message: 'Estado de deuda actualizado correctamente.',
        severity: 'success',
      });
      if (onReload) {
        onReload();
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      setSnackbar({
        open: true,
        message: apiMessage || 'No se pudo actualizar el estado de deuda.',
        severity: 'error',
      });
    } finally {
      setUpdatingContract(null);
    }
  };

  if (!rows.length) {
    return (
      <GeneralTableBox>
        <Typography variant="body1" color="text.secondary">
          No se encontraron registros que coincidan con los filtros seleccionados.
        </Typography>
      </GeneralTableBox>
    );
  }

  return (
    <Box>
      <GeneralTableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell>Órden</HeadTableCell>
              <HeadTableCell>Suscriptor</HeadTableCell>
              <HeadTableCell>Poseedor</HeadTableCell>
              <HeadTableCell>Forma pago</HeadTableCell>
              <HeadTableCell>Cupones en mora</HeadTableCell>
              <HeadTableCell>Importe mora</HeadTableCell>
              <HeadTableCell>Estado</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const subscriberNames = Array.isArray(row.subscriber_names)
                ? row.subscriber_names
                : [];

              return (
                <TableRow key={row.contract_uuid} hover>
                  <RowTableCell>{row.contract_number}</RowTableCell>
                  <RowTableCell sx={{ maxWidth: '300px', minWidth: '300px' }}>
                    {subscriberNames.length > 0 ? (
                      subscriberNames.map((name, index) => (
                        <Typography
                          key={`${row.contract_uuid}-subscriber-${index}`}
                          variant="body2"
                          fontWeight={400}
                          fontSize={12}
                        >
                          {name}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2">Sin suscriptor</Typography>
                    )}
                  </RowTableCell>
                  <RowTableCell>{row.is_possessor ? 'SI' : 'NO'}</RowTableCell>
                  <RowTableCell>{row.payment_method}</RowTableCell>
                  <RowTableCell>{row.overdue_installments}</RowTableCell>
                  <RowTableCell sx={{ maxWidth: '180px', minWidth: '180px' }}>
                    {formatCurrency(row.debt_amount)}
                  </RowTableCell>
                  <RowTableCell>
                    <CustomSelect
                      size="small"
                      value={row.debt_status}
                      onChange={(event) => handleStatusChange(row, event.target.value)}
                      sx={{ minWidth: 180 }}
                      disabled={updatingContract === row.contract_uuid}
                    >
                      {GERENCIALES_DEBT_STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </CustomSelect>
                  </RowTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </GeneralTableBox>
      {pagination?.pages > 1 && (
        <PaginationTable
          totalPages={pagination.pages}
          totalCount={pagination.total}
          page={pagination.page}
          onPageChange={onPageChange}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DebtStatusTable;
